import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError, money, dOnly, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea, statusTone } from "../ui";

const NEXT = {
    draft: ["pending_approval", "cancelled"],
    pending_approval: ["ordered", "draft", "cancelled"],
    ordered: ["cancelled"],
    partially_received: [],
    received: [],
    cancelled: [],
};

export default function RhmPurchaseOrders() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [create, setCreate] = useState(null);
    const [receive, setReceive] = useState(null);

    const { data: orders = [], isLoading } = useQuery({
        queryKey: ["rhm-pos"],
        queryFn: async () => (await rhm.get("/purchase-orders")).data,
    });
    const { data: suppliers = [] } = useQuery({
        queryKey: ["rhm-suppliers"],
        queryFn: async () => (await rhm.get("/suppliers")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["rhm-pos"] });
        qc.invalidateQueries({ queryKey: ["rhm-inventory"] });
        qc.invalidateQueries({ queryKey: ["rhm-all-batches"] });
        qc.invalidateQueries({ queryKey: ["rhm-dashboard"] });
    };

    const post = useMutation({
        mutationFn: async (payload) => (await rhm.post("/purchase-orders", payload)).data,
        onSuccess: () => { toast.success("Purchase order created"); setCreate(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const patch = useMutation({
        mutationFn: async ({ id, payload }) => (await rhm.patch(`/purchase-orders/${id}`, payload)).data,
        onSuccess: () => { toast.success("Purchase order updated"); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const doReceive = useMutation({
        mutationFn: async ({ id, lines }) => (await rhm.post(`/purchase-orders/${id}/receive`, { lines })).data,
        onSuccess: () => { toast.success("Stock received into inventory"); setReceive(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const openBlank = () => setCreate({
        supplier_id: suppliers[0]?.id || "", expected_delivery: "", notes: "", status: "draft",
        items: [{ product_id: products[0]?.id || "", quantity: "", purchase_price: "", batch_number: "", mfg_date: "", expiry_date: "" }],
    });

    const prefill = async (productId) => {
        try {
            const { data } = await rhm.get(`/purchase-orders/suggest/${productId}`);
            setCreate({
                supplier_id: data.supplier_id || suppliers[0]?.id || "", expected_delivery: "", notes:
                    `Auto-suggested: stock ${data.current_stock} vs minimum ${data.min_stock}`, status: "draft",
                items: [{
                    product_id: productId, quantity: data.suggested_quantity || "",
                    purchase_price: data.purchase_price ?? "", batch_number: "", mfg_date: "", expiry_date: "",
                }],
            });
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div data-testid="rhm-po-page">
            <PageHead title="Purchase Orders" sub="Raise, approve and receive stock against suppliers."
                action={can("purchases.create") && (
                    <Btn onClick={openBlank} data-testid="rhm-add-po">
                        <Plus className="w-3.5 h-3.5 inline mr-1.5" /> NEW PURCHASE ORDER
                    </Btn>
                )} />

            {can("purchases.create") && (
                <Card title="Reorder Suggestions" className="mb-5">
                    <div className="flex flex-wrap gap-3">
                        {products.filter((p) => p.low_stock && p.status !== "discontinued").map((p) => (
                            <button key={p.id} onClick={() => prefill(p.id)} data-testid={`rhm-po-suggest-${p.product_code}`}
                                className="border border-gold/40 bg-gold/5 px-4 py-3 text-left hover:bg-gold/10">
                                <div className="text-sm font-semibold">{p.name}</div>
                                <div className="mono text-[9px] tracking-[0.16em] text-gold mt-1">
                                    {p.stock?.available} LEFT · REORDER {p.reorder_qty}
                                </div>
                            </button>
                        ))}
                        {!products.some((p) => p.low_stock) && <Empty msg="No product is below its minimum stock level." />}
                    </div>
                </Card>
            )}

            <Card title={`${orders.length} purchase order(s)`}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-po-table" head={["PO", "Supplier", "Lines", "Value", "Expected", "Status", "Actions"]}
                        empty={!orders.length && <Empty msg="No purchase orders yet." />}>
                        {orders.map((po) => (
                            <tr key={po.id} data-testid={`rhm-po-row-${po.po_number}`}>
                                <Td className="mono text-xs text-gold">{po.po_number}</Td>
                                <Td className="font-semibold">{po.supplier_name}</Td>
                                <Td className="text-sm text-zinc-400">
                                    {po.items.map((i) => `${i.product_code} × ${i.quantity}`).join(", ")}
                                </Td>
                                <Td>{po.total_value ? money(po.total_value) : "—"}</Td>
                                <Td className="text-sm">{po.expected_delivery ? dOnly(po.expected_delivery) : "—"}</Td>
                                <Td><Badge tone={statusTone(po.status)}>{titleCase(po.status)}</Badge></Td>
                                <Td>
                                    <div className="flex flex-wrap gap-2">
                                        {(NEXT[po.status] || []).map((s) => (
                                            <button key={s} onClick={() => patch.mutate({ id: po.id, payload: { status: s } })}
                                                data-testid={`rhm-po-${po.po_number}-to-${s}`}
                                                className="mono text-[9px] tracking-[0.14em] border border-white/15 px-2 py-1 hover:border-gold hover:text-gold">
                                                {titleCase(s).toUpperCase()}
                                            </button>
                                        ))}
                                        {["ordered", "partially_received"].includes(po.status) && can("purchases.approve") && (
                                            <button onClick={() => setReceive({ ...po, lines: po.items.map((i, idx) => ({
                                                index: idx, received_qty: i.quantity - (i.received_qty || 0),
                                                batch_number: i.batch_number || "", mfg_date: i.mfg_date || "",
                                                expiry_date: i.expiry_date || "", purchase_price: i.purchase_price ?? "",
                                            })) })}
                                                data-testid={`rhm-po-${po.po_number}-receive`}
                                                className="mono text-[9px] tracking-[0.14em] bg-gold text-black px-2 py-1">RECEIVE</button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!create} onClose={() => setCreate(null)} title="New Purchase Order" wide testId="rhm-po-modal">
                {create && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            post.mutate({
                                ...create,
                                expected_delivery: create.expected_delivery || null,
                                items: create.items.map((i) => ({
                                    ...i,
                                    quantity: Number(i.quantity || 0),
                                    purchase_price: i.purchase_price === "" ? null : Number(i.purchase_price),
                                    batch_number: i.batch_number || null,
                                    mfg_date: i.mfg_date || null,
                                    expiry_date: i.expiry_date || null,
                                })),
                            });
                        }}
                        className="space-y-5"
                    >
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label="Supplier" required>
                                <Select required value={create.supplier_id} onChange={(e) => setCreate({ ...create, supplier_id: e.target.value })} data-testid="po-supplier">
                                    <option value="">Select supplier</option>
                                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </Select>
                            </Field>
                            <Field label="Expected delivery">
                                <Input type="date" value={create.expected_delivery} onChange={(e) => setCreate({ ...create, expected_delivery: e.target.value })} />
                            </Field>
                            <Field label="Status">
                                <Select value={create.status} onChange={(e) => setCreate({ ...create, status: e.target.value })}
                                    options={[{ value: "draft", label: "Draft" }, { value: "pending_approval", label: "Pending Approval" }]} />
                            </Field>
                        </div>

                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">LINES</div>
                            {create.items.map((item, idx) => (
                                <div key={idx} className="border border-white/10 p-4 mb-3 grid md:grid-cols-3 gap-3">
                                    <Field label="Product" required>
                                        <Select required value={item.product_id}
                                            onChange={(e) => setCreate({ ...create, items: create.items.map((x, i) => i === idx ? { ...x, product_id: e.target.value } : x) })}
                                            data-testid={`po-line-${idx}-product`}>
                                            <option value="">Select</option>
                                            {products.map((p) => <option key={p.id} value={p.id}>{p.product_code} · {p.name}</option>)}
                                        </Select>
                                    </Field>
                                    <Field label="Quantity" required>
                                        <Input type="number" min="1" required value={item.quantity}
                                            onChange={(e) => setCreate({ ...create, items: create.items.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x) })}
                                            data-testid={`po-line-${idx}-qty`} />
                                    </Field>
                                    <Field label="Purchase price (₹)" hint="Optional">
                                        <Input type="number" min="0" step="0.01" value={item.purchase_price}
                                            onChange={(e) => setCreate({ ...create, items: create.items.map((x, i) => i === idx ? { ...x, purchase_price: e.target.value } : x) })} />
                                    </Field>
                                    <Field label="Batch number">
                                        <Input value={item.batch_number} onChange={(e) => setCreate({ ...create, items: create.items.map((x, i) => i === idx ? { ...x, batch_number: e.target.value } : x) })} />
                                    </Field>
                                    <Field label="Manufacturing date">
                                        <Input type="date" value={item.mfg_date} onChange={(e) => setCreate({ ...create, items: create.items.map((x, i) => i === idx ? { ...x, mfg_date: e.target.value } : x) })} />
                                    </Field>
                                    <Field label="Expiry date">
                                        <Input type="date" value={item.expiry_date} onChange={(e) => setCreate({ ...create, items: create.items.map((x, i) => i === idx ? { ...x, expiry_date: e.target.value } : x) })} />
                                    </Field>
                                </div>
                            ))}
                            <button type="button" onClick={() => setCreate({ ...create, items: [...create.items, { product_id: "", quantity: "", purchase_price: "", batch_number: "", mfg_date: "", expiry_date: "" }] })}
                                className="mono text-[10px] tracking-[0.16em] text-gold">+ ADD LINE</button>
                        </div>

                        <Field label="Notes"><Textarea rows={2} value={create.notes} onChange={(e) => setCreate({ ...create, notes: e.target.value })} /></Field>

                        <div className="flex gap-3">
                            <Btn type="submit" disabled={post.isPending} data-testid="po-submit">CREATE PURCHASE ORDER</Btn>
                            <Btn type="button" variant="ghost" onClick={() => setCreate(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal open={!!receive} onClose={() => setReceive(null)} title={`Receive stock — ${receive?.po_number || ""}`} wide testId="rhm-receive-modal">
                {receive && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            doReceive.mutate({
                                id: receive.id,
                                lines: receive.lines.map((l) => ({
                                    ...l,
                                    received_qty: Number(l.received_qty || 0),
                                    purchase_price: l.purchase_price === "" ? null : Number(l.purchase_price),
                                    batch_number: l.batch_number || null,
                                    mfg_date: l.mfg_date || null,
                                    expiry_date: l.expiry_date || null,
                                })),
                            });
                        }}
                        className="space-y-4"
                    >
                        {receive.lines.map((l, idx) => {
                            const item = receive.items[l.index];
                            return (
                                <div key={idx} className="border border-white/10 p-4">
                                    <div className="text-sm font-semibold mb-3">
                                        {item.product_name} · ordered {item.quantity}, already received {item.received_qty || 0}
                                    </div>
                                    <div className="grid md:grid-cols-4 gap-3">
                                        <Field label="Receiving now" required>
                                            <Input type="number" min="0" required value={l.received_qty}
                                                onChange={(e) => setReceive({ ...receive, lines: receive.lines.map((x, i) => i === idx ? { ...x, received_qty: e.target.value } : x) })}
                                                data-testid={`receive-${idx}-qty`} />
                                        </Field>
                                        <Field label="Batch number">
                                            <Input value={l.batch_number} onChange={(e) => setReceive({ ...receive, lines: receive.lines.map((x, i) => i === idx ? { ...x, batch_number: e.target.value } : x) })}
                                                data-testid={`receive-${idx}-batch`} />
                                        </Field>
                                        <Field label="Manufacturing date">
                                            <Input type="date" value={l.mfg_date} onChange={(e) => setReceive({ ...receive, lines: receive.lines.map((x, i) => i === idx ? { ...x, mfg_date: e.target.value } : x) })} />
                                        </Field>
                                        <Field label="Expiry date">
                                            <Input type="date" value={l.expiry_date} onChange={(e) => setReceive({ ...receive, lines: receive.lines.map((x, i) => i === idx ? { ...x, expiry_date: e.target.value } : x) })}
                                                data-testid={`receive-${idx}-expiry`} />
                                        </Field>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="flex gap-3">
                            <Btn type="submit" disabled={doReceive.isPending} data-testid="receive-submit">RECEIVE INTO INVENTORY</Btn>
                            <Btn type="button" variant="ghost" onClick={() => setReceive(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
